import { useUploadPictureUrlMutation } from "@/features/management-settings/hooks/mutations/use-upload-picture-url.mutation";
import { toAppLanguage } from "@/i18n/to-app-language";
import { showAlertCallout } from "@/shared/alerts";
import {
  getGetMeQueryKey,
  getMe,
  useCompleteOnboarding,
  useGetMeSuspense,
} from "@/spz-api/user/user";
import { useAuth0 } from "@auth0/auth0-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ONBOARDING_FORM_DEFAULT_VALUES } from "../forms/constants/onboarding-form.constants";
import {
  type OnboardingFormValues,
  onboardingFormSchema,
} from "../forms/schemas/onboarding-form.schema";

interface UseOnboardingFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useOnboardingForm = ({
  onSuccess,
  onError,
}: UseOnboardingFormProps = {}) => {
  const { t } = useTranslation();
  const { user, isLoading: isAuthLoading } = useAuth0();
  const { data: me } = useGetMeSuspense();
  const queryClient = useQueryClient();

  const { mutateAsync: updateProfile, isPending: isSubmitting } =
    useCompleteOnboarding();

  const onboardingSchema = onboardingFormSchema(t);

  const defaultValues: OnboardingFormValues = {
    ...ONBOARDING_FORM_DEFAULT_VALUES,
    email: me.email || ONBOARDING_FORM_DEFAULT_VALUES.email,
    firstName: me.firstName || ONBOARDING_FORM_DEFAULT_VALUES.firstName,
    lastName: me.lastName || ONBOARDING_FORM_DEFAULT_VALUES.lastName,
    preferredLanguage: toAppLanguage(
      me.preferredLanguage || ONBOARDING_FORM_DEFAULT_VALUES.preferredLanguage,
    ),
    notificationsEnabled:
      me.notificationsEnabled ??
      ONBOARDING_FORM_DEFAULT_VALUES.notificationsEnabled,
    file: ONBOARDING_FORM_DEFAULT_VALUES.file,
  };

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues,
    mode: "onBlur",
  });

  // Memoize reset handler to prevent unnecessary re-renders
  const handleFormReset = useCallback(
    (userData: typeof user) => {
      if (!userData) return;

      const _preferredLanguage =
        me.preferredLanguage ||
        userData.locale?.substring(0, 2) ||
        ONBOARDING_FORM_DEFAULT_VALUES.preferredLanguage;

      form.reset({
        email:
          me.email || userData.email || ONBOARDING_FORM_DEFAULT_VALUES.email,
        firstName:
          me.firstName ||
          userData.given_name ||
          ONBOARDING_FORM_DEFAULT_VALUES.firstName,
        lastName:
          me.lastName ||
          userData.family_name ||
          ONBOARDING_FORM_DEFAULT_VALUES.lastName,
        preferredLanguage: toAppLanguage(_preferredLanguage),
        notificationsEnabled:
          me.notificationsEnabled ??
          ONBOARDING_FORM_DEFAULT_VALUES.notificationsEnabled,
        file: ONBOARDING_FORM_DEFAULT_VALUES.file,
      });
    },
    [form, me],
  );
  // Handle form reset when user data is available
  useEffect(() => {
    if (!isAuthLoading && user) {
      handleFormReset(user);
    }
  }, [user, isAuthLoading, handleFormReset]);

  const uploadPictureUrlMutation = useUploadPictureUrlMutation();

  const onSubmit = async (data: OnboardingFormValues) => {
    try {
      let attachmentId: string | undefined;
      if (data.file) {
        const result = await uploadPictureUrlMutation.mutateAsync({
          file: data.file,
        });
        attachmentId = result?.id;
      }

      await updateProfile({
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          preferredLanguage: data.preferredLanguage,
          notificationsEnabled: data.notificationsEnabled,
          profilePictureId: attachmentId,
        },
      });
      const updatedUser = await getMe();
      queryClient.setQueryData([getGetMeQueryKey()], updatedUser);
      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t("profile_update_error");

      showAlertCallout({
        type: "critical",
        title: t("profile_update_error"),
        description: errorMessage,
      });

      onError?.(error instanceof Error ? error : new Error(errorMessage));

      console.error("[useOnboardingForm] Submit error:", error);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    isLoading: isAuthLoading,
  };
};
